import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react'
import { useFilterStorage } from '../../../hooks/useFilterStorage'
const toDateInputValue = (date: Date): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

export interface SellerReportDateRange {
  startDate: string
  finalDate: string
}

export const createDefaultSellerReportDateRange = (): SellerReportDateRange => {
  const today = toDateInputValue(new Date())

  return {
    startDate: today,
    finalDate: today,
  }
}

const normalizeDateRange = (value: unknown): SellerReportDateRange => {
  const defaults = createDefaultSellerReportDateRange()

  if (!value || typeof value !== 'object') {
    return defaults
  }

  const stored = value as Partial<SellerReportDateRange>

  return {
    startDate: typeof stored.startDate === 'string' && stored.startDate ? stored.startDate : defaults.startDate,
    finalDate: typeof stored.finalDate === 'string' && stored.finalDate ? stored.finalDate : defaults.finalDate,
  }
}

export const useSellerReportFilters = <T extends SellerReportDateRange>(initialFilters: T): {
  filters: T
  setFilters: Dispatch<SetStateAction<T>>
  isReady: boolean
} => {
  const { loadFilters, saveFilters, storageKey } = useFilterStorage('seller-reports')
  const [filters, setFiltersState] = useState<T>(initialFilters)
  const [isReady, setIsReady] = useState(false)
  const hydratedStorageKeyRef = useRef<string | null>(null)

  useEffect(() => {
    if (hydratedStorageKeyRef.current === storageKey) {
      return
    }

    hydratedStorageKeyRef.current = storageKey
    setFiltersState((currentFilters) => ({
      ...currentFilters,
      ...normalizeDateRange(loadFilters()),
    }))
    setIsReady(true)
  }, [loadFilters, storageKey])

  const setFilters = useCallback<Dispatch<SetStateAction<T>>>(
    (nextFilters) => {
      setFiltersState((previousFilters) => {
        const resolvedFilters =
          typeof nextFilters === 'function' ? nextFilters(previousFilters) : nextFilters

        saveFilters({
          startDate: resolvedFilters.startDate,
          finalDate: resolvedFilters.finalDate,
        })
        return resolvedFilters
      })
    },
    [saveFilters]
  )

  return { filters, setFilters, isReady }
}